import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Heart, MessageCircle, Send, Star, Navigation, Play, Music2 } from "lucide-react";
import { api } from "@/lib/api";
import { IdentityLabel } from "@/components/ui-bits";

const DEFAULT_FEED = [
  "/assets/portfolio-21.webp",
  "/assets/portfolio-15.webp",
  "/assets/portfolio-7.webp",
  "/assets/portfolio-5.webp",
  "/assets/portfolio-4.webp",
];

const STAGES = [
  { key: "ig", label: "Instagram", color: "#E1306C", desc: "Feed & konten yang konsisten dan menjual." },
  { key: "tt", label: "TikTok", color: "#111", desc: "Video pendek yang menjangkau audiens baru." },
  { key: "maps", label: "Google Maps", color: "#34A853", desc: "Bisnis Anda mudah ditemukan pelanggan lokal." },
];

export default function PhoneShowcase({ section }) {
  const ref = useRef(null);
  const [stage, setStage] = useState(0);
  const { data: home } = useQuery({ queryKey: ["home"], queryFn: async () => (await api.get("/home")).data });
  const sec = section || (home?.sections || []).find((s) => s.key === "showcase") || {};
  const FEED = (sec.ig_images && sec.ig_images.length ? sec.ig_images : DEFAULT_FEED);
  const TIKTOK = sec.tiktok_image || FEED[3] || DEFAULT_FEED[3];
  const heading = sec.title || "Satu studio untuk seluruh presence digital";
  const desc = sec.description || "Dari feed Instagram, video TikTok, sampai Google Maps—Digitalis membangun kehadiran digital brand Anda secara menyeluruh dan terukur.";
  return (
    <section id="digitalisasi" ref={ref} className="relative bg-surface py-16 md:py-24" data-testid="phone-showcase">
      <div className="relative flex items-center overflow-hidden">
        <div className="relative mx-auto grid w-full max-w-[1400px] grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)] items-center gap-x-3 gap-y-8 px-5 md:px-10 lg:grid-cols-2 lg:gap-10">
          {/* Left copy */}
          <div className="col-span-2 lg:col-span-1 lg:col-start-1 lg:row-start-1">
            <IdentityLabel>Digitalisasi Bisnis Anda</IdentityLabel>
            <h2 className="font-display font-extrabold tracking-tightest leading-[1.08] text-3xl md:text-5xl mb-6">
              {heading}
            </h2>
            <p className="max-w-md text-base text-muted-foreground md:text-lg lg:mb-10">
              {desc}
            </p>
          </div>

          <div className="col-start-1 row-start-2 min-w-0 self-center lg:col-start-1 lg:row-start-2">
            <div className="space-y-2">
              {STAGES.map((s, i) => (
                <button
                  key={s.key}
                  onClick={() => setStage(i)}
                  className={`flex w-full min-w-0 items-center gap-2 rounded-xl border p-2 text-left transition-all min-[420px]:gap-3 min-[420px]:p-3 lg:gap-4 lg:rounded-2xl lg:p-4 ${stage === i ? "border-primary bg-card soft-shadow" : "border-border/60 opacity-60 hover:opacity-100"}`}
                  data-testid={`stage-${s.key}`}
                >
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white min-[420px]:h-8 min-[420px]:w-8 lg:h-9 lg:w-9 lg:text-sm" style={{ background: s.color }}>{i + 1}</span>
                  <div className="min-w-0">
                    <p className="break-words text-xs font-semibold leading-tight min-[420px]:text-sm lg:text-base">{s.label}</p>
                    <p className="mt-1 hidden text-xs leading-snug text-muted-foreground min-[420px]:block lg:text-sm">{s.desc}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Phone */}
          <div className="col-start-2 row-start-2 flex min-w-0 justify-center [perspective:1400px] lg:col-start-2 lg:row-span-2 lg:row-start-1">
            <div className="relative">
              <div className="relative h-[290px] w-[140px] rounded-[2rem] border border-white/10 bg-neutral-900 p-1.5 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.45)] min-[420px]:h-[373px] min-[420px]:w-[180px] min-[420px]:p-2 sm:h-[478px] sm:w-[230px] lg:h-[580px] lg:w-[280px] lg:rounded-[2.6rem] lg:p-2.5 dark:bg-black" data-testid="phone-device">
                <div className="absolute left-1/2 top-1.5 z-20 h-4 w-20 -translate-x-1/2 rounded-full bg-black min-[420px]:top-2 min-[420px]:h-5 min-[420px]:w-24 lg:top-2.5 lg:h-6 lg:w-28" data-testid="phone-notch" />
                <div className="relative h-full w-full overflow-hidden rounded-[1.6rem] bg-white min-[420px]:rounded-[1.8rem] lg:rounded-[2.1rem] dark:bg-neutral-900">
                  <ScreenIG active={stage === 0} feed={FEED} />
                  <ScreenTikTok active={stage === 1} img={TIKTOK} />
                  <ScreenMaps active={stage === 2} />
                </div>
              </div>
              {/* floating chips */}
              <FloatingChip show={stage === 0} className="-left-10 top-20" color="#E1306C" icon={<Heart className="w-4 h-4" />} text="+1.240 likes" />
              <FloatingChip show={stage === 1} className="-right-12 top-32" color="#111" icon={<Play className="w-4 h-4" />} text="128K views" />
              <FloatingChip show={stage === 2} className="-left-8 bottom-24" color="#34A853" icon={<Star className="w-4 h-4" />} text="4.9 rating" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FloatingChip({ show, className, color, icon, text }) {
  return (
    <motion.div
      initial={false}
      animate={{ opacity: show ? 1 : 0, y: show ? 0 : 12, scale: show ? 1 : 0.9 }}
      transition={{ duration: 0.5 }}
      className={`absolute ${className} glass rounded-2xl px-4 py-2.5 border border-border soft-shadow hidden sm:flex items-center gap-2 z-30`}
    >
      <span className="w-7 h-7 rounded-full flex items-center justify-center text-white" style={{ background: color }}>{icon}</span>
      <span className="text-sm font-semibold whitespace-nowrap">{text}</span>
    </motion.div>
  );
}

const screenBase = "absolute inset-0 transition-opacity duration-500";

function ScreenIG({ active, feed = [] }) {
  const imgs = [...feed, ...feed, ...feed, ...feed].slice(0, 12);
  return (
    <div className={`${screenBase} ${active ? "opacity-100" : "opacity-0"} bg-white dark:bg-neutral-900`} data-testid="phone-screen-ig">
      <div className="flex items-center gap-1 border-b border-neutral-200 px-1.5 pb-2 pt-6 min-[420px]:gap-2 min-[420px]:px-3 min-[420px]:pb-3 min-[420px]:pt-8 lg:pt-10 dark:border-neutral-800" data-testid="screen-ig-header">
        <div className="h-5 w-5 rounded-full bg-gradient-to-tr from-[#E1306C] to-[#F77737] p-[1px] min-[420px]:h-8 min-[420px]:w-8 min-[420px]:p-[2px]"><div className="w-full h-full rounded-full bg-white dark:bg-neutral-900" /></div>
        <div className="min-w-0 flex-1"><p className="truncate text-[7px] font-bold text-neutral-900 min-[420px]:text-[11px] dark:text-white">digitalis.id</p><p className="truncate text-[6px] text-neutral-400 min-[420px]:text-[9px]">Medan, Indonesia</p></div>
        <span className="rounded bg-[#E1306C] px-1 py-0.5 text-[6px] font-bold text-white min-[420px]:rounded-md min-[420px]:px-2 min-[420px]:py-1 min-[420px]:text-[9px]">Follow</span>
      </div>
      <div className="flex justify-around border-b border-neutral-100 py-1 text-center min-[420px]:py-2 dark:border-neutral-800">
        {[["128", "Posts"], ["24.5K", "Followers"], ["312", "Following"]].map(([n, l]) => (
          <div key={l}><p className="text-[8px] font-bold text-neutral-900 min-[420px]:text-[11px] dark:text-white">{n}</p><p className="text-[6px] text-neutral-400 min-[420px]:text-[8px]">{l}</p></div>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-[2px]">
        {imgs.map((src, i) => (
          <div key={i} className="aspect-square overflow-hidden"><img src={src} alt="" className="w-full h-full object-cover" loading="lazy" /></div>
        ))}
      </div>
    </div>
  );
}

function ScreenTikTok({ active, img }) {
  return (
    <div className={`${screenBase} ${active ? "opacity-100" : "opacity-0"} bg-black`} data-testid="phone-screen-tiktok">
      <img src={img} alt="" className="absolute inset-0 w-full h-full object-cover opacity-90" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30" />
      <div className="absolute left-0 right-0 top-6 flex justify-center gap-2 text-[7px] font-semibold text-white min-[420px]:top-8 min-[420px]:gap-4 min-[420px]:text-xs lg:top-10" data-testid="screen-tiktok-nav">
        <span className="opacity-60">Following</span><span className="border-b-2 border-white pb-0.5">For You</span>
      </div>
      <div className="absolute bottom-14 right-1.5 flex flex-col items-center gap-2 text-white min-[420px]:bottom-24 min-[420px]:right-3 min-[420px]:gap-5">
        <div className="flex flex-col items-center gap-0.5"><Heart className="h-4 w-4 fill-white min-[420px]:h-6 min-[420px]:w-6" /><span className="text-[6px] min-[420px]:text-[9px]">128K</span></div>
        <div className="flex flex-col items-center gap-0.5"><MessageCircle className="h-4 w-4 fill-white min-[420px]:h-6 min-[420px]:w-6" /><span className="text-[6px] min-[420px]:text-[9px]">2.4K</span></div>
        <div className="flex flex-col items-center gap-0.5"><Send className="h-4 w-4 min-[420px]:h-6 min-[420px]:w-6" /><span className="text-[6px] min-[420px]:text-[9px]">Share</span></div>
      </div>
      <div className="tiktok-caption absolute bottom-4 left-1.5 right-7 text-white min-[420px]:bottom-8 min-[420px]:left-3 min-[420px]:right-16">
        <p className="text-[8px] font-bold min-[420px]:text-sm">@digitalis.id</p>
        <p className="text-xs opacity-90 mt-1">Konten kuliner yang bikin ngiler 🔥 #medankuliner</p>
        <div className="mt-1 flex items-center gap-1 text-[6px] min-[420px]:mt-2 min-[420px]:text-[10px]"><Music2 className="h-2 w-2 min-[420px]:h-3 min-[420px]:w-3" /> original sound - digitalis</div>
      </div>
    </div>
  );
}

function ScreenMaps({ active }) {
  return (
    <div className={`${screenBase} ${active ? "opacity-100" : "opacity-0"} bg-[#e8eef3] dark:bg-neutral-800`} data-testid="phone-screen-maps">
      <div className="absolute inset-0 z-10 overflow-hidden bg-[#e8e6e1] text-neutral-800">
        <svg className="absolute inset-0 h-full w-full" viewBox="0 0 240 500" preserveAspectRatio="none" aria-hidden="true">
          <rect width="240" height="500" fill="#e8e6e1" />
          <path d="M-25 108 C35 88 65 122 125 100 S205 54 270 74" fill="none" stroke="#d2d0ca" strokeWidth="23" />
          <path d="M-25 108 C35 88 65 122 125 100 S205 54 270 74" fill="none" stroke="#fffdf8" strokeWidth="17" />
          <path d="M25 -20 C50 72 28 150 58 222 S102 360 82 530" fill="none" stroke="#d2d0ca" strokeWidth="19" />
          <path d="M25 -20 C50 72 28 150 58 222 S102 360 82 530" fill="none" stroke="#fffdf8" strokeWidth="13" />
          <path d="M190 -25 C164 70 190 152 164 238 S125 390 147 525" fill="none" stroke="#d2d0ca" strokeWidth="22" />
          <path d="M190 -25 C164 70 190 152 164 238 S125 390 147 525" fill="none" stroke="#fffdf8" strokeWidth="15" />
          <path d="M-20 285 C48 258 116 302 260 250" fill="none" stroke="#d8c98a" strokeWidth="18" />
          <path d="M-20 285 C48 258 116 302 260 250" fill="none" stroke="#fff3b0" strokeWidth="12" />
          <path d="M-15 402 C52 365 128 430 265 385" fill="none" stroke="#d2d0ca" strokeWidth="15" />
          <path d="M-15 402 C52 365 128 430 265 385" fill="none" stroke="#fffdf8" strokeWidth="10" />
          <g fill="#d7ded1">
            <path d="M88 18h52v55H88z" /><path d="M83 133h57v78H83z" /><path d="M8 153h31v72H8z" />
            <path d="M184 120h48v86h-48z" /><path d="M8 316h50v55H8z" /><path d="M168 305h63v61h-63z" />
          </g>
          <g fill="#c7dfbd"><path d="M92 320c25-15 46-8 55 16-5 29-30 43-53 27-13-15-13-29-2-43z" /><path d="M142 112c15-11 29-5 34 10-3 17-17 24-32 15-9-8-9-17-2-25z" /></g>
        </svg>

        <div className="absolute left-2 right-2 top-6 flex items-center gap-1.5 rounded-full bg-white px-2 py-1.5 shadow-md min-[420px]:left-3 min-[420px]:right-3 min-[420px]:top-8 min-[420px]:px-3 min-[420px]:py-2 lg:top-10" data-testid="screen-maps-search">
          <span className="h-2 w-2 rounded-full bg-[#4285F4]" />
          <span className="truncate text-[6px] font-medium text-neutral-600 min-[420px]:text-[9px]">Digitalis, Medan</span>
        </div>
        <span className="absolute left-[46%] top-[21%] rotate-[-8deg] text-[5px] font-medium text-neutral-500 min-[420px]:text-[7px]">Jl. Putri Hijau</span>
        <span className="absolute left-[8%] top-[53%] rotate-[-5deg] text-[5px] font-medium text-neutral-500 min-[420px]:text-[7px]">Jl. Guru Patimpus</span>
        <span className="absolute right-[8%] top-[41%] text-[5px] font-semibold text-neutral-500 min-[420px]:text-[7px]">Kesawan</span>

        <div className="absolute left-1/2 top-[38%] -translate-x-1/2">
          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-[#EA4335] text-white shadow-lg min-[420px]:h-10 min-[420px]:w-10"><Navigation className="h-3.5 w-3.5 fill-white min-[420px]:h-5 min-[420px]:w-5" /></div>
          <span className="mx-auto block h-2 w-2 -translate-y-1 rotate-45 bg-[#EA4335]" />
        </div>

        <div className="absolute bottom-2 left-1.5 right-1.5 rounded-xl bg-white p-2 shadow-lg min-[420px]:bottom-4 min-[420px]:left-3 min-[420px]:right-3 min-[420px]:rounded-2xl min-[420px]:p-3">
          <p className="text-[7px] font-bold leading-tight text-neutral-900 min-[420px]:text-[10px]">Digitalis — Digital Marketing Spesialist</p>
          <div className="my-1 flex items-center gap-0.5" aria-label="Rating 5 bintang">{[0, 1, 2, 3, 4].map((i) => <Star key={i} className="h-2.5 w-2.5 fill-[#FBBC04] text-[#FBBC04] min-[420px]:h-3 min-[420px]:w-3" />)}<span className="ml-1 text-[6px] font-semibold text-neutral-600 min-[420px]:text-[8px]">5,0</span></div>
          <p className="text-[6px] font-semibold text-[#1a73e8] min-[420px]:text-[8px]">Rute · Simpan · Bagikan</p>
        </div>
      </div>
    </div>
  );
}
